import { useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import FinishDialog from "@/components/FinishDialog";
import DynamicDetailsView, { hasAnyValue } from "@/components/DynamicDetailsView";
import AttachmentsSection from "@/components/AttachmentsSection";
import { formatDuration } from "@/lib/format";
import {
  ChevronDown, ChevronRight, FolderKanban, User, HardHat, Timer,
  Play, CheckCircle2, History,
} from "lucide-react";
import { toast } from "sonner";

function fmtDT(s) {
  if (!s) return "-";
  try { return new Date(s).toLocaleString("tr-TR"); } catch { return s; }
}

export default function UstaProjectRow({ project, stages = [], categories = [] }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(false);

  // Item detail modal
  const [detailItem, setDetailItem] = useState(null);
  const [detailLogs, setDetailLogs] = useState([]);

  // Takeover picker
  const [takeoverItem, setTakeoverItem] = useState(null);
  const [takeoverStage, setTakeoverStage] = useState("");

  // Finish dialog
  const [finishOpen, setFinishOpen] = useState(false);
  const [finishTarget, setFinishTarget] = useState(null);

  const loadItems = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/projects/${project.id}/items`);
      setItems(r.data);
    } catch { setItems([]); }
    finally { setLoading(false); }
  };

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && items === null) await loadItems();
  };

  const myActiveOn = (it) =>
    (it.active_takeovers || []).find(t => t.user_id === user?.id);

  const openDetail = async (it) => {
    setDetailItem(it);
    try {
      const r = await api.get(`/projects/${project.id}/items/${it.id}/logs`);
      setDetailLogs(r.data);
    } catch { setDetailLogs([]); }
  };

  const startTakeover = (it) => {
    setTakeoverItem(it);
    setTakeoverStage(it.manufacturing_stage || "");
  };

  const confirmTakeover = async () => {
    if (!takeoverStage) { toast.error("Aşama seçin"); return; }
    try {
      await api.post(`/projects/${project.id}/items/${takeoverItem.id}/takeover`, {
        stage: takeoverStage,
      });
      toast.success(`"${takeoverStage}" aşaması başlatıldı`);
      setTakeoverItem(null);
      setDetailItem(null);
      await loadItems();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Başlatılamadı");
    }
  };

  const finishWork = (it) => {
    setFinishTarget({ projectId: project.id, itemId: it.id, itemName: it.name });
    setFinishOpen(true);
    setDetailItem(null);
  };

  return (
    <Card className="border border-zinc-200 shadow-none overflow-hidden" data-testid={`usta-project-${project.id}`}>
      <button
        type="button"
        onClick={toggle}
        className="w-full px-4 py-3 flex items-center gap-3 text-left hover:bg-zinc-50 transition"
        data-testid={`usta-project-header-${project.id}`}
      >
        {open ? <ChevronDown size={16} className="text-zinc-400 shrink-0" /> : <ChevronRight size={16} className="text-zinc-400 shrink-0" />}
        <FolderKanban size={16} className="text-zinc-400 shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="font-semibold truncate">{project.name}</div>
          <div className="text-xs text-zinc-500 flex items-center gap-2 mt-0.5 flex-wrap">
            <span className="inline-flex items-center gap-1"><User size={11}/>{project.customer_name}</span>
            <span className="text-zinc-300">·</span>
            <span>Başlangıç: {project.start_date?.slice(0,10) || "-"}</span>
            {project.end_date && (
              <>
                <span className="text-zinc-300">·</span>
                <span>Bitiş: {project.end_date.slice(0,10)}</span>
              </>
            )}
          </div>
        </div>
      </button>

      {open && (
        <div className="border-t border-zinc-100 bg-zinc-50/40 p-3" data-testid={`usta-project-items-${project.id}`}>
          {loading && <div className="p-3 text-sm text-zinc-500">Yükleniyor...</div>}
          {!loading && items && items.length === 0 && (
            <div className="p-3 text-sm text-zinc-500 text-center">Bu projede henüz ürün yok.</div>
          )}
          {!loading && items && items.length > 0 && (
            <div className="grid gap-3">
              {items.map(it => {
                const takeovers = it.active_takeovers || [];
                const mine = myActiveOn(it);
                return (
                  <Card
                    key={it.id}
                    data-testid={`usta-item-${it.id}`}
                    onClick={() => openDetail(it)}
                    className="p-4 border border-zinc-200 shadow-none cursor-pointer hover:shadow bg-white transition"
                  >
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="md:col-span-2 min-w-0">
                        <div className="font-semibold text-base truncate">{it.name}</div>
                        {it.manufacturing_stage && (
                          <div className="mt-1">
                            <span className="inline-block px-2 py-0.5 rounded text-xs bg-amber-50 text-amber-700 border border-amber-200">
                              <HardHat size={10} className="inline mr-0.5"/>Mevcut Aşama: {it.manufacturing_stage}
                            </span>
                          </div>
                        )}
                        <dl className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
                          <div className="flex gap-1.5"><dt className="text-zinc-500 shrink-0">Malzeme:</dt><dd className="font-medium text-zinc-800">{it.material || <span className="text-zinc-400">—</span>}</dd></div>
                          <div className="flex gap-1.5"><dt className="text-zinc-500 shrink-0">Renk:</dt><dd className="font-medium text-zinc-800">{it.color || <span className="text-zinc-400">—</span>}</dd></div>
                          <div className="flex gap-1.5"><dt className="text-zinc-500 shrink-0">Model:</dt><dd className="font-medium text-zinc-800">{it.model || <span className="text-zinc-400">—</span>}</dd></div>
                          <div className="flex gap-1.5"><dt className="text-zinc-500 shrink-0">Miktar:</dt><dd className="font-medium text-zinc-800">{Number(it.quantity || 0)} {it.unit || ""}</dd></div>
                        </dl>
                        {(it.notes || "").trim() && (
                          <div className="mt-2 text-xs bg-zinc-50 rounded p-2 border border-zinc-100">
                            <span className="text-zinc-500">Notlar: </span>
                            <span className="text-zinc-800 whitespace-pre-wrap">{it.notes}</span>
                          </div>
                        )}
                      </div>

                      <div className="md:border-l md:border-zinc-100 md:pl-4 flex flex-col gap-2" onClick={(e)=>e.stopPropagation()}>
                        <div className="text-xs uppercase tracking-widest text-zinc-500">
                          Devralanlar ({takeovers.length})
                        </div>
                        {takeovers.length === 0 ? (
                          <div className="text-xs text-zinc-400">Kimse devralmamış</div>
                        ) : (
                          <ul className="space-y-1.5">
                            {takeovers.map(t => (
                              <li key={t.id} className={`text-xs flex items-center gap-1 rounded border px-2 py-1 ${t.user_id === user?.id ? "bg-blue-50 border-blue-200 text-blue-800" : "bg-white border-zinc-200 text-zinc-700"}`}>
                                <User size={11} />
                                <span className="font-medium truncate">{t.user_name}{t.user_id === user?.id && <span className="text-zinc-500 font-normal"> (siz)</span>}</span>
                                <span className="text-zinc-400">·</span>
                                <span className="truncate">{t.stage}</span>
                                <span className="text-zinc-400">·</span>
                                <span className="inline-flex items-center gap-0.5 font-medium">
                                  <Timer size={10} />{formatDuration(t.started_at, null)}
                                </span>
                              </li>
                            ))}
                          </ul>
                        )}
                        <div className="mt-auto pt-2">
                          {mine ? (
                            <Button
                              size="sm"
                              className="w-full bg-emerald-600 hover:bg-emerald-700"
                              onClick={() => finishWork(it)}
                              data-testid={`usta-item-finish-${it.id}`}
                            >
                              <CheckCircle2 size={14} className="mr-1" />İş Tamamlandı
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              className="w-full bg-zinc-900 hover:bg-zinc-800"
                              onClick={() => startTakeover(it)}
                              data-testid={`usta-item-start-${it.id}`}
                            >
                              <Play size={14} className="mr-1" />İşe Başla
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Item detail modal */}
      <Dialog open={!!detailItem} onOpenChange={(v)=>{ if(!v) setDetailItem(null); }}>
        <DialogContent data-testid="usta-item-detail-dialog" className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Ürün Detayı</DialogTitle>
            <DialogDescription>
              {detailItem && (
                <>
                  <span className="font-medium text-zinc-800">{detailItem.name}</span>
                  {" — "}
                  <span>{project.name}</span> · <span>{project.customer_name}</span>
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          {detailItem && (
            <div className="space-y-4">
              {detailItem.manufacturing_stage && (
                <div>
                  <span className="inline-block px-2 py-0.5 rounded text-xs bg-amber-50 text-amber-700 border border-amber-200">
                    <HardHat size={11} className="inline mr-0.5"/>Mevcut Aşama: {detailItem.manufacturing_stage}
                  </span>
                </div>
              )}

              <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                <div><dt className="text-xs uppercase tracking-widest text-zinc-500">Malzeme</dt><dd className="mt-0.5">{detailItem.material || <span className="text-zinc-400">—</span>}</dd></div>
                <div><dt className="text-xs uppercase tracking-widest text-zinc-500">Renk</dt><dd className="mt-0.5">{detailItem.color || <span className="text-zinc-400">—</span>}</dd></div>
                <div><dt className="text-xs uppercase tracking-widest text-zinc-500">Model</dt><dd className="mt-0.5">{detailItem.model || <span className="text-zinc-400">—</span>}</dd></div>
                <div><dt className="text-xs uppercase tracking-widest text-zinc-500">Miktar</dt><dd className="mt-0.5">{Number(detailItem.quantity || 0)} {detailItem.unit || ""}</dd></div>
                {(detailItem.notes || "").trim() && (
                  <div className="col-span-2"><dt className="text-xs uppercase tracking-widest text-zinc-500">Notlar</dt><dd className="mt-0.5 whitespace-pre-wrap">{detailItem.notes}</dd></div>
                )}
              </dl>

              {(() => {
                const cat = categories.find(c => c.id === detailItem.category_id);
                if (!cat || (cat.fields || []).length === 0) return null;
                if (!hasAnyValue(cat.fields, detailItem.details)) return null;
                return (
                  <div data-testid="item-detail-details">
                    <div className="text-xs uppercase tracking-widest text-zinc-500 mb-2">{cat.name} — İmalat Detayları</div>
                    <DynamicDetailsView fields={cat.fields} value={detailItem.details || {}} testIdPrefix="item-detail-view" />
                  </div>
                );
              })()}

              <div>
                <div className="text-xs uppercase tracking-widest text-zinc-500 mb-2">
                  Aktif Devralanlar ({(detailItem.active_takeovers || []).length})
                </div>
                {(detailItem.active_takeovers || []).length === 0 ? (
                  <div className="text-sm text-zinc-500">Şu anda ürünü devralan yok.</div>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {detailItem.active_takeovers.map(t => (
                      <div key={t.id} className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs border ${t.user_id === user?.id ? "bg-blue-50 border-blue-200 text-blue-800" : "bg-amber-50 border-amber-200 text-amber-700"}`}>
                        <User size={11} />
                        <span className="font-medium">{t.user_name}</span>
                        <span className="text-zinc-500">·</span>
                        <span>{t.stage}</span>
                        <span className="text-zinc-500">·</span>
                        <span className="inline-flex items-center gap-0.5 font-medium">
                          <Timer size={10}/>{formatDuration(t.started_at, null)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <div className="text-xs uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-1">
                  <History size={12}/> İşlem Geçmişi ({detailLogs.length})
                </div>
                {detailLogs.length === 0 ? (
                  <div className="text-sm text-zinc-500">Henüz kayıt yok.</div>
                ) : (
                  <div className="max-h-56 overflow-auto rounded-md border border-zinc-200">
                    <table className="acc">
                      <thead><tr><th>Usta</th><th>Aşama</th><th>Başlangıç</th><th>Bitiş</th><th>Süre</th></tr></thead>
                      <tbody>
                        {detailLogs.map(l => (
                          <tr key={l.id}>
                            <td className="font-medium">{l.user_name}</td>
                            <td><span className="inline-block px-2 py-0.5 rounded text-xs bg-zinc-100 text-zinc-700">{l.stage}</span></td>
                            <td className="text-xs text-zinc-600">{fmtDT(l.started_at)}</td>
                            <td className="text-xs text-zinc-600">{fmtDT(l.finished_at)}</td>
                            <td className="text-xs">
                              {l.finished_at
                                ? <span className="text-zinc-700">{formatDuration(l.started_at, l.finished_at)}</span>
                                : <span className="text-blue-700 font-medium">{formatDuration(l.started_at, null)}</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <AttachmentsSection
                ownerKind="project_item"
                ownerId={detailItem.id}
                title="Fotoğraf & Dosyalar"
                accept="image/*,application/pdf"
                readOnly={!myActiveOn(detailItem)}
                testIdPrefix="usta-item-attachments"
              />
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDetailItem(null)}>Kapat</Button>
            {detailItem && (myActiveOn(detailItem)
              ? (
                <Button
                  className="bg-emerald-600 hover:bg-emerald-700"
                  onClick={() => finishWork(detailItem)}
                  data-testid="usta-detail-finish-button"
                ><CheckCircle2 size={14} className="mr-1"/>İş Tamamlandı</Button>
              )
              : (
                <Button
                  className="bg-zinc-900 hover:bg-zinc-800"
                  onClick={() => startTakeover(detailItem)}
                  data-testid="usta-detail-start-button"
                ><Play size={14} className="mr-1"/>İşe Başla</Button>
              )
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Takeover stage picker */}
      <Dialog open={!!takeoverItem} onOpenChange={(v)=>{ if(!v) setTakeoverItem(null); }}>
        <DialogContent data-testid="usta-takeover-dialog">
          <DialogHeader>
            <DialogTitle>İşe Başla</DialogTitle>
            <DialogDescription>
              <span className="font-medium text-zinc-800">{takeoverItem?.name}</span> için hangi imalat aşamasını başlatıyorsunuz?
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label>Aşama</Label>
            <Select value={takeoverStage} onValueChange={setTakeoverStage}>
              <SelectTrigger data-testid="usta-takeover-stage-select"><SelectValue placeholder="Seçin" /></SelectTrigger>
              <SelectContent>
                {stages.map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setTakeoverItem(null)}>İptal</Button>
            <Button
              className="bg-zinc-900 hover:bg-zinc-800"
              onClick={confirmTakeover}
              data-testid="usta-takeover-confirm"
            ><Play size={14} className="mr-1"/>Başlat</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <FinishDialog
        open={finishOpen}
        onOpenChange={setFinishOpen}
        projectId={finishTarget?.projectId}
        itemId={finishTarget?.itemId}
        itemName={finishTarget?.itemName}
        stages={stages}
        onFinished={loadItems}
      />
    </Card>
  );
}
